import { UserManager, WebStorageStateStore } from "oidc-client-ts"
import { ENV } from "~/ENV"
import { scopeString } from "./scopes"

export const AUTH_CALLBACK_PATH = "/callback"
// export const AUTH_CALLBACK_PATH = "/auth/callback"
export const SILENT_CALLBACK_PATH = "/auth/silent-callback"
export const LOGOUT_CALLBACK_PATH = "/auth/logout"

const RENEW_LEAD_SECONDS = 60

/**
 * `window.location.origin` carries the current scheme, which is http in local
 * dev. Lifeway Account only accepts https redirect URIs, so build them from the
 * host and pin the scheme.
 */
const authUrl = (path: string) => `https://${window.location.host}${path}`

/**
 * Modelled on mylifeway-frontend/src/client/js/app/utils/userManager.js, pointed
 * at the INT Lifeway Account and scoped for the DCE graph.
 *
 * Built lazily: `expose.tsx` is imported eagerly by the federation host, so
 * constructing a UserManager at module scope would run before the host is ready.
 */
const createUserManager = (): UserManager =>
  new UserManager({
    authority: ENV.LIFEWAY_ACCOUNT_URL,
    client_id: ENV.LIFEWAY_ACCOUNT_CLIENT_ID,
    redirect_uri: authUrl(AUTH_CALLBACK_PATH),
    silent_redirect_uri: authUrl(SILENT_CALLBACK_PATH),
    post_logout_redirect_uri: authUrl(LOGOUT_CALLBACK_PATH),
    response_type: "code",
    scope: scopeString(),
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: RENEW_LEAD_SECONDS,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  })

let shared: UserManager | undefined

/**
 * The one UserManager per document. `AuthProvider` subscribes to its events, so
 * the redirect callback has to finish the code exchange on this same instance:
 * a second UserManager writes the user to session storage but raises
 * `userLoaded` only on itself, leaving the provider signed out until the next
 * full page load — which is what made sign in take two round trips.
 */
export const getUserManager = (): UserManager => {
  shared ??= createUserManager()

  return shared
}
